import { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, RefreshCw, CheckCircle2, ArrowRight, Search, ExternalLink } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import api from '../../lib/api';
import { toast } from 'sonner';

const ACTION_LABELS = {
  reassign: 'Reassign in MySQL (UPDATE parserdata.UserId)',
  add_entity: 'Add entity row (co-register account under bill\'s current customer)',
  keep: 'Keep — mark reviewed without changing MySQL',
};

const ACTION_DESCRIPTIONS = {
  reassign: 'The bill will move to the rightful owner\'s org on the next sync. Use when the bill was misrouted to the wrong inbox.',
  add_entity: 'Both customers will own this account. Use when the property is co-managed by both companies.',
  keep: 'No MySQL change. The bill stays hidden from end-org users but exits this report queue.',
};

export default function CrossCustomerReportPage() {
  const [data, setData] = useState({ items: [], total: 0, unresolved: 0, unresolved_spend: 0 });
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [filter, setFilter] = useState('');
  const [includeResolved, setIncludeResolved] = useState(false);
  const [resolveDialog, setResolveDialog] = useState(null); // {bill, action, note}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get('/super-admin/reports/cross-customer', {
        params: { include_resolved: includeResolved, limit: 1000 },
      });
      setData(d);
    } catch (e) {
      toast.error('Failed to load cross-customer report');
    } finally {
      setLoading(false);
    }
  }, [includeResolved]);

  useEffect(() => { load(); }, [load]);

  const runScan = async () => {
    setScanning(true);
    try {
      const { data: d } = await api.post('/super-admin/reports/cross-customer/scan');
      toast.success(`Scan done: flagged=${d.stats.flagged}, unflagged=${d.stats.unflagged}, scanned=${d.stats.scanned}`);
      await load();
    } catch (e) {
      toast.error('Scan failed');
    } finally {
      setScanning(false);
    }
  };

  const submitResolve = async () => {
    if (!resolveDialog) return;
    const { bill, action, note } = resolveDialog;
    try {
      await api.post(`/super-admin/reports/cross-customer/${bill.bill_id}/resolve`, { action, note });
      toast.success(`Resolved as "${action}"`);
      setResolveDialog(null);
      await load();
    } catch (e) {
      toast.error(`Resolution failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const items = data.items.filter((it) => {
    if (!filter) return true;
    const f = filter.toLowerCase();
    return [
      it.account_number, it.vendor_name, it.bill_arrived_under?.name,
      it.rightful_owner?.customer_name, it.rightful_owner?.site_name, it.reason,
    ].some((s) => s && String(s).toLowerCase().includes(f));
  });

  return (
    <div className="space-y-4" data-testid="cross-customer-report">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-6 w-6 text-amber-600 mt-1" />
          <div>
            <h2 className="text-xl font-bold">Cross-Customer Data</h2>
            <p className="text-sm text-muted-foreground max-w-3xl">
              Bills whose arrival inbox doesn&apos;t match the customer the account is registered to in
              <code className="mx-1 px-1 py-0.5 text-xs bg-muted rounded">entity</code> /
              <code className="mx-1 px-1 py-0.5 text-xs bg-muted rounded">botaccounts</code> /
              <code className="mx-1 px-1 py-0.5 text-xs bg-muted rounded">ubm_*_accounts</code>.
              These bills are <strong>hidden from end-org users</strong> until resolved here.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={runScan}
            disabled={scanning}
            data-testid="cross-customer-scan-btn"
          >
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${scanning ? 'animate-spin' : ''}`} />
            {scanning ? 'Scanning…' : 'Re-scan All Bills'}
          </Button>
        </div>
      </div>

      {/* Stat tiles */}
      <div className="grid grid-cols-3 gap-3">
        <StatTile label="Unresolved Bills" value={data.unresolved} accent="rose" testid="stat-unresolved" />
        <StatTile label="Unresolved Spend" value={`$${(data.unresolved_spend || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`} accent="amber" testid="stat-spend" />
        <StatTile label="Total Flagged (incl. resolved)" value={data.total} accent="slate" testid="stat-total" />
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative w-80">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search account, vendor, customer, site, reason…"
            className="w-full pl-8 pr-3 py-2 text-sm border border-border rounded-none bg-background focus:outline-none focus:ring-1 focus:ring-primary"
            data-testid="cross-customer-search"
          />
        </div>
        <label className="text-xs flex items-center gap-1.5 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={includeResolved}
            onChange={(e) => setIncludeResolved(e.target.checked)}
            data-testid="cross-customer-include-resolved"
          />
          Show resolved
        </label>
      </div>

      {/* Table */}
      <div className="border border-border rounded-none overflow-hidden bg-card">
        <table className="w-full text-xs">
          <thead className="bg-muted/40 border-b border-border">
            <tr>
              <th className="text-left px-3 py-2">Account #</th>
              <th className="text-left px-3 py-2">Vendor / Date / $</th>
              <th className="text-left px-3 py-2">Bill arrived under</th>
              <th className="text-left px-3 py-2 w-8"></th>
              <th className="text-left px-3 py-2">Rightful owner</th>
              <th className="text-left px-3 py-2">Source</th>
              <th className="text-right px-3 py-2 w-32">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading && items.length === 0 && (
              <tr><td colSpan={7} className="text-center py-8 text-muted-foreground">Loading…</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={7} className="text-center py-8 text-muted-foreground">
                <CheckCircle2 className="h-5 w-5 inline-block mr-2 text-emerald-600" />
                No cross-customer anomalies. Everything is clean.
              </td></tr>
            )}
            {items.map((it) => (
              <tr key={it.bill_id} className="border-b border-border hover:bg-muted/20" data-testid={`row-${it.bill_id}`}>
                <td className="px-3 py-2 font-mono">{it.account_number || '—'}</td>
                <td className="px-3 py-2">
                  <div>{it.vendor_name || '—'}</div>
                  <div className="text-muted-foreground text-[10px]">
                    {it.invoice_date || '?'} · ${(it.total_amount_due || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </div>
                </td>
                <td className="px-3 py-2">
                  <div className="font-medium">{it.bill_arrived_under?.name || '—'}</div>
                  <div className="text-[10px] font-mono text-muted-foreground truncate max-w-[180px]">
                    {it.bill_arrived_under?.mysql_user_id?.slice(0, 12)}…
                  </div>
                </td>
                <td className="px-1 py-2 text-muted-foreground"><ArrowRight className="h-3.5 w-3.5" /></td>
                <td className="px-3 py-2">
                  <div className="font-medium text-emerald-700 dark:text-emerald-400">
                    {it.rightful_owner?.customer_name || '—'}
                  </div>
                  <div className="text-[10px] text-muted-foreground">
                    {it.rightful_owner?.site_name || '—'}
                  </div>
                </td>
                <td className="px-3 py-2">
                  <Badge variant="outline" className="text-[10px]">
                    {it.rightful_owner?.source || '?'}
                  </Badge>
                </td>
                <td className="px-3 py-2 text-right">
                  {it.resolved_at ? (
                    <Badge variant="secondary" className="text-[10px]">
                      <CheckCircle2 className="h-3 w-3 mr-1" /> {it.resolution_action}
                    </Badge>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-[11px]"
                      onClick={() => setResolveDialog({ bill: it, action: 'reassign', note: '' })}
                      data-testid={`resolve-${it.bill_id}`}
                    >
                      Resolve <ExternalLink className="h-3 w-3 ml-1" />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Resolution Dialog */}
      <Dialog open={!!resolveDialog} onOpenChange={(o) => !o && setResolveDialog(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Resolve Cross-Customer Bill</DialogTitle>
            <DialogDescription>
              {resolveDialog && (
                <>
                  <span className="font-mono text-xs">{resolveDialog.bill.account_number}</span>
                  {' · '}{resolveDialog.bill.vendor_name}{' · $'}
                  {(resolveDialog.bill.total_amount_due || 0).toLocaleString()}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          {resolveDialog && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-3 p-3 bg-muted/30 border border-border rounded-none">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Bill arrived under</div>
                  <div className="font-medium">{resolveDialog.bill.bill_arrived_under?.name}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Rightful owner ({resolveDialog.bill.rightful_owner?.source})</div>
                  <div className="font-medium text-emerald-700 dark:text-emerald-400">
                    {resolveDialog.bill.rightful_owner?.customer_name}
                  </div>
                  <div className="text-xs text-muted-foreground">{resolveDialog.bill.rightful_owner?.site_name}</div>
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold block mb-1.5">Resolution Action</label>
                <Select
                  value={resolveDialog.action}
                  onValueChange={(v) => setResolveDialog({ ...resolveDialog, action: v })}
                >
                  <SelectTrigger data-testid="resolve-action-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(ACTION_LABELS).map(([k, label]) => (
                      <SelectItem key={k} value={k}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1.5">{ACTION_DESCRIPTIONS[resolveDialog.action]}</p>
              </div>

              <div>
                <label className="text-xs font-semibold block mb-1.5">Note (optional)</label>
                <Textarea
                  value={resolveDialog.note}
                  onChange={(e) => setResolveDialog({ ...resolveDialog, note: e.target.value })}
                  placeholder="Why this resolution? (visible in audit log)"
                  className="rounded-none text-sm"
                  rows={2}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setResolveDialog(null)}>Cancel</Button>
            <Button onClick={submitResolve} data-testid="resolve-confirm-btn">
              Confirm Resolution
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatTile({ label, value, accent, testid }) {
  const colorMap = {
    rose: 'border-l-rose-500',
    amber: 'border-l-amber-500',
    slate: 'border-l-slate-500',
  };
  return (
    <div
      className={`border border-border border-l-4 ${colorMap[accent] || ''} bg-card px-4 py-3 rounded-none`}
      data-testid={testid}
    >
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-2xl font-bold mt-0.5">{value}</div>
    </div>
  );
}
